/**
 * Backlog-field API — the read hook plus the six CRUD mutation hooks for a
 * project's configurable backlog schema. Reads are open to any authenticated
 * project member; writes require PROJECT_MANAGE (enforced server-side).
 * Mutation hooks mirror config.ts's useCreateConfigOption/useUpdateConfigOption/
 * useDeleteConfigOption pattern verbatim: mutationFn posts/puts/patches/deletes,
 * onSuccess invalidates the ['backlog-field', projectId] prefix so both the
 * active-only and include-archived caches refresh (TanStack Query's prefix
 * matching covers the trailing includeArchived key segment automatically).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  BacklogField, BacklogFieldKind, BacklogFieldOption, BacklogFieldType, BacklogSystemField,
} from '@/types/backlogField';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
}

async function fetchBacklogFields(projectId: string, includeArchived: boolean): Promise<BacklogField[]> {
  const res = await api.get<ApiEnvelope<BacklogField[]>>('/backlog-field', {
    params: { projectId, includeArchived },
  });
  return res.data.data;
}

/**
 * A project's backlog schema, non-archived fields only by default. Pass
 * includeArchived=true for the Table settings view, which also lists hidden
 * fields (for the Archived-columns disclosure). staleTime matches config
 * lists — schema changes are rare relative to record edits, and consumers
 * (table/form/drawer/builder) all read the same cached result.
 */
export function useBacklogFields(projectId: string, includeArchived = false) {
  return useQuery({
    queryKey: ['backlog-field', projectId, includeArchived],
    queryFn: () => fetchBacklogFields(projectId, includeArchived),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations — every write invalidates the ['backlog-field', projectId] prefix.
// ---------------------------------------------------------------------------

function invalidateBacklogFields(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  void qc.invalidateQueries({ queryKey: ['backlog-field', projectId] });
}

/** Create body — mirrors CreateBacklogFieldRequest. sortOrder is omitted:
 *  the backend appends new fields at maxSortOrder + 1 when it's absent. */
export interface CreateBacklogFieldPayload {
  projectId: string;
  kind: BacklogFieldKind;
  systemRef?: BacklogSystemField | null;
  label: string;
  dataType?: BacklogFieldType | null;
  options?: BacklogFieldOption[] | null;
  required: boolean;
}

/** Update body — mirrors UpdateBacklogFieldRequest's mutable subset. kind/
 *  systemRef/dataType are deliberately absent: the DTO's own doc comment
 *  says they exist server-side only to detect (and reject) an attempted
 *  change — the FE never sends them under normal use. */
export interface UpdateBacklogFieldPayload {
  label: string;
  options?: BacklogFieldOption[] | null;
  required: boolean;
}

export function useCreateBacklogField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBacklogFieldPayload) =>
      api.post<ApiEnvelope<BacklogField>>('/backlog-field', payload).then((res) => res.data.data),
    onSuccess: () => invalidateBacklogFields(qc, projectId),
  });
}

export function useUpdateBacklogField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBacklogFieldPayload }) =>
      api.put<ApiEnvelope<BacklogField>>(`/backlog-field/${id}`, payload).then((res) => res.data.data),
    onSuccess: () => invalidateBacklogFields(qc, projectId),
  });
}

/** Bulk reorder — the full permutation of the project's non-archived field
 *  ids, in the desired order. Backend densifies sort_order to 0..N-1. */
export function useReorderBacklogFields(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put<ApiEnvelope<void>>('/backlog-field/order', { projectId, orderedIds }).then((res) => res.data.data),
    onSuccess: () => invalidateBacklogFields(qc, projectId),
  });
}

export function useArchiveBacklogField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<ApiEnvelope<void>>(`/backlog-field/${id}/archive`).then((res) => res.data.data),
    onSuccess: () => invalidateBacklogFields(qc, projectId),
  });
}

export function useRestoreBacklogField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<ApiEnvelope<void>>(`/backlog-field/${id}/restore`).then((res) => res.data.data),
    onSuccess: () => invalidateBacklogFields(qc, projectId),
  });
}

export function useDeleteBacklogField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<ApiEnvelope<void>>(`/backlog-field/${id}`).then((res) => res.data.data),
    onSuccess: () => invalidateBacklogFields(qc, projectId),
  });
}
